<template>
  <div class="tc-back" @click.self="$emit('close')">
    <div class="tc" role="dialog" aria-modal="true" :aria-label="me.label + ' team'" :style="{ '--tc': me.bright }">
      <div class="tc-bar">
        <span class="tc-name">{{ me.label }}</span>
        <span v-if="gameData && me.units.length" class="lx-grade tc-grade" :style="{ background: tg.color }" v-tip="'Team grade ' + tg.grade + ' · expected win rate ~' + tg.score + '%'">{{ tg.grade }}</span>
        <ChampStreakBadge v-if="me.key === 'champion' && champStreak" :streak="champStreak" />
        <span class="tc-fate">{{ path.fate }}</span>
        <button class="lx-btn-ghost tc-x" @click="$emit('close')" aria-label="Close">✕</button>
      </div>

      <p v-if="!me.units.length" class="tc-empty">No roster yet.</p>
      <template v-else>
        <!-- on a phone the four sections become tabs (and the units show one at a time) so the card isn't a mile long -->
        <div v-if="narrow" class="tc-tabs tc-sections" role="tablist">
          <button v-for="t in sectionTabs" :key="t.key" role="tab" class="tc-tab" :class="{ on: tab === t.key }" :aria-selected="String(tab === t.key)" @click="tab = t.key">{{ t.label }}</button>
        </div>

        <!-- the team at a glance -->
        <div v-show="show('summary')" class="tc-summary">
          <div class="tc-tile"><span>Grade</span><b :style="{ color: tg.color }">{{ tg.grade }}</b><i>~{{ tg.score }}% expected</i></div>
          <div class="tc-tile"><span>Total HP</span><b>{{ totals.hp }}</b><i>{{ totals.mp }} MP</i></div>
          <div class="tc-tile"><span>Avg Speed</span><b>{{ totals.speed }}</b><i>Move {{ totals.move }} · Jump {{ totals.jump }}</i></div>
          <div class="tc-tile"><span>Avg Brave / Faith</span><b>{{ totals.brave }} / {{ totals.faith }}</b><i>Evade {{ totals.evade }}% front</i></div>
          <div class="tc-turns">
            <span class="tc-lbl">Turn order by Speed</span>
            <span v-for="(u, k) in bySpeed" :key="k" class="tc-turn"><b>{{ k + 1 }}</b> {{ u.Name }} <i>{{ stat(u, 'speed') }}</i></span>
          </div>
          <div class="tc-els">
            <span class="tc-lbl">Elements across the team</span>
            <span v-if="!teamElements.length" class="tc-none">No elemental gear.</span>
            <span v-for="e in teamElements" :key="e.k + e.el" class="tc-el" :class="[e.k, { shared: e.names.length > 1 }]" v-tip="e.names.join(', ')">{{ e.label }} {{ e.el }}<b v-if="e.names.length > 1"> ×{{ e.names.length }}</b></span>
          </div>
        </div>

        <!-- all four units in full -->
        <h3 v-if="!narrow" class="tc-h">The team</h3>
        <div v-if="narrow" v-show="show('units')" class="tc-tabs" role="tablist">
          <button v-for="(u, k) in me.units" :key="k" role="tab" class="tc-tab" :class="{ on: unitAt === k }" :aria-selected="String(unitAt === k)" @click="unitAt = k">{{ u.Name }}</button>
        </div>
        <div v-show="show('units')" class="tc-units">
          <div v-for="(u, k) in me.units" v-show="!narrow || unitAt === k" :key="k" class="tc-unit" :class="{ boss: u.RaidBoss }">
            <button class="tc-u-head" @click="unitCard = u" v-tip="'Open ' + u.Name + '\'s full card'">
              <UnitSprite :unit="u" :team-color="me.pal" :fallback-src="portrait(u, me.label)" :animated="true" :size="64" />
              <span class="tc-u-id">
                <b :style="trackColor(u) ? { color: trackColor(u) } : null"><span v-if="u.RaidBoss" class="tc-boss" v-tip="'Raid boss'">★</span>{{ u.Name }}</b>
                <span class="lx-grade" :style="{ background: unitG(u).color }">{{ unitG(u).grade }}</span>
                <i>{{ u.Gender === 'Monster' ? '' : u.Gender + ' ' }}{{ u.Class }} · {{ u.Sign }}</i>
              </span>
            </button>
            <div class="tc-u-stats">
              <span v-for="s in statTiles(u)" :key="s.k" v-tip="s.tip"><i>{{ s.k }}</i><b>{{ s.v }}</b></span>
            </div>
            <EvadeGrid class="tc-u-ev" :ev="info(u).evade" :sources="info(u).sources" />
            <div v-if="u.Gender !== 'Monster'" class="tc-u-sec">Gear</div>
            <div v-for="g in (u.Gender === 'Monster' ? [] : gear(u))" :key="g.slot" class="tc-gear">
              <img v-if="g.name" :src="g.icon" :alt="g.name" @error="$event.target.style.visibility = 'hidden'">
              <span v-else class="tc-gear-empty"></span>
              <span><b>{{ g.name || '—' }}</b><i v-if="g.line">{{ g.line }}</i></span>
            </div>
            <div v-if="els(u).length" class="tc-u-els"><span v-for="e in els(u)" :key="e.k + e.el" class="tc-el" :class="e.k">{{ e.label }} {{ e.el }}</span></div>
            <div v-for="set in sets(u)" :key="set.title">
              <div class="tc-u-sec">{{ set.title }}</div>
              <span v-for="sk in set.skills" :key="sk.name" class="tc-skill" :style="sk.tier ? { borderColor: sk.tier.color } : {}" v-tip="sk.beast ? 'Needs a Beastmaster ally' : sk.tier ? sk.tier.label + ' · rolls ' + sk.roll + '%' : ''">{{ sk.name }}</span>
            </div>
            <div v-if="u.ReactionSkill || u.SupportSkill || u.MoveSkill" class="tc-u-sec">Reaction · Support · Move</div>
            <div v-if="u.ReactionSkill || u.SupportSkill || u.MoveSkill" class="tc-rsm">{{ u.ReactionSkill || '—' }} · {{ u.SupportSkill || '—' }} · {{ u.MoveSkill || '—' }}</div>
            <div v-if="innates(u).length" class="tc-innate">Innate: {{ innates(u).join(' · ') }}</div>
            <button class="lx-btn-ghost tc-u-full" @click="unitCard = u">Full card</button>
          </div>
        </div>

        <!-- against the team it faces next (or last faced) -->
        <div v-if="foe" v-show="show('vs')">
          <!-- next opponent not decided yet: the odds against each team that could still be it; click one for the detail -->
          <template v-if="possible">
            <h3 class="tc-h">Next: the {{ possibleMatch.round }} against one of these
              <small>{{ possibleMatch.title }}<template v-if="possibleMatch.mapName"> · {{ possibleMatch.mapName }}</template></small></h3>
            <div class="tc-cands">
              <button v-for="c in possibleRows" :key="c.key" class="tc-cand" :class="{ on: foe.key === c.key }" :style="{ '--oc': c.bright }"
                      @click="pickKey = c.key" v-tip="'Show ' + me.label + ' against ' + c.label + ' in detail'">
                <span class="tc-cand-name">{{ c.label }}<span v-if="c.grade" class="lx-grade" :style="{ background: c.grade.color }">{{ c.grade.grade }}</span></span>
                <span class="tc-cand-fate">{{ c.fate }}</span>
                <span v-if="c.mine == null" class="tc-cand-run">{{ c.odds === 'running' ? 'reading the odds…' : c.odds === 'failed' ? 'the sim couldn\'t run this one' : 'queued' }}</span>
                <template v-else>
                  <span class="tc-cand-word"><b :style="{ color: c.odds.favBright }">{{ c.odds.fav }}</b> {{ c.odds.word.toLowerCase() }}</span>
                  <span class="tc-cand-bar" v-tip="`${me.label} ${c.mine}% · ${c.label} ${100 - c.mine}%`"><i :style="{ width: c.mine + '%', background: me.bright }"></i><i :style="{ width: (100 - c.mine) + '%', background: c.bright }"></i></span>
                </template>
              </button>
            </div>
          </template>
          <h3 class="tc-h">{{ possible ? 'If it\'s' : path.next >= 0 ? 'Next up: against' : 'Last fight: against' }} <span :style="{ color: foe.bright }">{{ foe.label }}</span>
            <small v-if="!possible && foeMatch">{{ foeMatch.title }}<template v-if="foeMatch.mapName"> · {{ foeMatch.mapName }}</template></small></h3>
          <div class="tc-vs">
            <div class="tc-odds"><HeroOdds :o="odds" :hero="{ l: me, r: foe }" :bar="true" /></div>
            <table class="tc-zod">
              <caption>Zodiac</caption>
              <tr><th></th><th v-for="(f, j) in foe.units" :key="j" :style="{ color: foe.bright }">{{ f.Name }}<i>{{ f.Sign }}</i></th></tr>
              <tr v-for="(u, k) in me.units" :key="k">
                <th>{{ u.Name }}<i>{{ u.Sign }}</i></th>
                <td v-for="(f, j) in foe.units" :key="j" :class="zod(u, f).toLowerCase()">{{ zod(u, f) === 'Neutral' ? '·' : zod(u, f) }}</td>
              </tr>
            </table>
          </div>
          <div class="tc-tabs" role="tablist">
            <button v-for="(u, k) in me.units" :key="k" role="tab" class="tc-tab" :class="{ on: vsAt === k }" :aria-selected="String(vsAt === k)" @click="vsAt = k">{{ u.Name }}</button>
          </div>
          <UnitVsPanel :key="vsAt" :unit="me.units[vsAt]" :side="me" :foe="foe" :gd="gameData" :fallback="portrait(me.units[vsAt], me.label)" :bare="true" />
        </div>

        <!-- its run this tournament -->
        <h3 v-if="!narrow" class="tc-h">This tournament</h3>
        <div v-show="show('run')" class="tc-run">
          <div v-for="m in runRows" :key="m.i" class="tc-run-row" :class="m.result">
            <span class="tc-run-round">{{ m.title }}</span>
            <span class="tc-run-map">{{ m.mapName || '' }}</span>
            <span class="tc-run-opp">vs <b :style="m.oppBright ? { color: m.oppBright } : null">{{ m.oppLabel }}</b></span>
            <span class="tc-run-res">{{ m.result === 'won' ? 'Won' : m.result === 'lost' ? 'Lost' : m.i === currentMatchIndex ? 'Fighting now' : 'Upcoming' }}</span>
          </div>
          <p v-if="me.key === 'champion' && champStreak" class="tc-reign">The reigning champion: {{ champStreak }} title defense{{ champStreak === 1 ? '' : 's' }} in a row.</p>
        </div>
      </template>
    </div>
    <UnitCardNew v-if="unitCard" :unit="unitCard" :side="me" :foe="foe" :gd="gameData" :fallback="portrait(unitCard, me.label)"
                 @close="unitCard = null" />
  </div>
</template>

<script>
// The full team card (2026-10 refresh): one team, everything at once. A summary strip (grade, total HP, speed, turn
// order, the elements it absorbs / halves / is weak to across all four units), all four units in full side by side, the
// team it faces next (or last faced) with the worded odds, a zodiac grid and each unit's options against it
// (UnitVsPanel), and its run through this bracket. Opened from the team section, the spotlight's banners and the
// bracket's team names; a unit opens its own full card (UnitCardNew) on top.
import UnitSprite from '../UnitSprite.vue';
import ChampStreakBadge from '../ChampStreakBadge.vue';
import EvadeGrid from '../spotlight/EvadeGrid.vue';
import HeroOdds from '../spotlight/HeroOdds.vue';
import UnitVsPanel from '../spotlight/UnitVsPanel.vue';
import UnitCardNew from '../spotlight/UnitCardNew.vue';
import modalA11y from '../../mixins/modalA11y.js';
import { unitInfo, gearRows, elementChips, skillSets, ELEMENT_KINDS } from '../spotlight/unitInfo.js';
import { cachedMatchOdds } from '../spotlight/matchOdds.js';
import { fmtStat, endlessTip, avgStat } from '../../lib/statDisplay.js';
import { teamPath, possibleFoes, TITLES, ROUND } from './teamPath.js';
import { matchColors } from '../../lib/spriteForClass.js';
import { teamBright } from '../../lib/teamColors.js';
import { zodiacCompat } from '../../lib/zodiac.js';
import { UNITS_BASE } from '../../lib/assetUrls.js';
import { unitGrade, teamGrade } from '../../lib/unitGrade.js';
import { rosterStats } from '../../lib/preload.js';

const MAP_RE = /^\s*(\d+)\)\s*(.*)$/;

export default {
  name: 'TeamCard',
  components: { UnitSprite, ChampStreakBadge, EvadeGrid, HeroOdds, UnitVsPanel, UnitCardNew },
  mixins: [modalA11y],
  props: {
    teamName: { type: String, required: true },         // 'red' … 'champion'
    teams: { type: Array, required: true },
    winners: { type: Array, default: () => [] },
    maps: { type: Array, default: () => [] },
    currentMatchIndex: { type: Number, default: -1 },
    gameData: { type: Object, required: true },
    champStreak: { type: Number, default: 0 },
    highlightColors: { type: [Map, Object], default: () => new Map() },
  },
  emits: ['close'],
  data() { return { unitCard: null, vsAt: 0, oddsMap: {}, pickKey: null, narrow: false, tab: 'summary', unitAt: 0 }; },
  computed: {
    byName() { const m = {}; for (const t of this.teams) m[t.name] = t; return m; },
    label() { return (n) => (n === 'champion' ? 'the Champion' : (this.byName[n] || {}).label || n); },
    path() { return teamPath(this.teamName, this.winners, this.label); },
    sides() {
      const t = this.byName[this.teamName] || { name: this.teamName, label: this.teamName, roster: [] };
      const f = this.foeKey && this.byName[this.foeKey];
      const mk = (x) => ({ key: x.name, label: x.label, units: x.roster || [], palettes: x.palettes, bright: teamBright(x.label) });
      const me = mk(t), foe = f ? mk(f) : null;
      if (foe) [me.pal, foe.pal] = matchColors(t, f);
      else [me.pal] = matchColors(t, null);
      return { me, foe };
    },
    me() { return this.sides.me; },
    sectionTabs() {
      const t = [{ key: 'summary', label: 'Overview' }, { key: 'units', label: 'Units' }];
      if (this.foe) t.push({ key: 'vs', label: this.possible ? 'Next foes' : 'vs ' + this.foe.label });
      return t.concat([{ key: 'run', label: 'Run' }]);
    },
    foe() { return this.sides.foe; },
    // the next match's other side, when it isn't decided yet: { at, keys } (see teamPath.possibleFoes)
    possible() {
      const p = possibleFoes(this.teamName, this.winners);
      return p && p.keys.some((k) => this.byName[k] && (this.byName[k].roster || []).length) ? p : null;
    },
    possibleMatch() { const p = this.possible; return p ? { ...this.matchInfo(p.at), round: ROUND[p.at] } : null; },
    // the opponent the detail below is about: the picked possible one (else the first), or the known next / last one
    foeKey() { const p = this.possible; return p ? (p.keys.includes(this.pickKey) ? this.pickKey : p.keys[0]) : this.path.foeKey; },
    foeAt() { return this.possible ? this.possible.at : this.path.foeAt; },
    odds() { const o = this.foe ? this.oddsMap[this.oddsKeyFor(this.foe.key, this.foeAt)] : null; return o && o !== 'failed' ? o : null; },
    // the possible opponents, the biggest threat (our lowest chance) first once the odds are in
    possibleRows() {
      const p = this.possible; if (!p) return [];
      const rows = p.keys.map((k) => this.byName[k]).filter((t) => t && (t.roster || []).length).map((t) => {
        const o = this.oddsMap[this.oddsKeyFor(t.name, p.at)] || null;
        return { key: t.name, label: t.label, bright: teamBright(t.label), grade: this.gameData ? teamGrade(t.roster, rosterStats, this.gameData) : null,
          fate: teamPath(t.name, this.winners, this.label).fate, odds: o, mine: o && typeof o === 'object' ? Math.round(100 * o.pA) : null };
      });
      return rows.sort((a, b) => (a.mine == null) - (b.mine == null) || (a.mine ?? 0) - (b.mine ?? 0));
    },
    // the odds to run: the detailed opponent first, then every possible one
    oddsJobs() {
      const jobs = [];
      if (this.foe) jobs.push([this.foe.key, this.foeAt]);
      if (this.possible) for (const k of this.possible.keys) if (k !== (this.foe && this.foe.key)) jobs.push([k, this.possible.at]);
      return jobs;
    },
    tg() { return teamGrade(this.me.units, rosterStats, this.gameData); },
    foeMatch() { const i = this.foeAt; return i >= 0 ? this.matchInfo(i) : null; },
    runRows() {
      return this.path.matches.map((m) => {
        const o = m.oppKey && this.byName[m.oppKey];
        return { ...m, ...this.matchInfo(m.i), oppBright: o ? teamBright(o.label) : null };
      });
    },
    totals() {
      const us = this.me.units, n = us.length || 1, sum = (f) => us.reduce((a, u) => a + (Number(f(u)) || 0), 0);
      const st = (k) => sum((u) => this.stat(u, k));
      return {
        hp: st('hp'), mp: st('mp'), speed: (st('speed') / n).toFixed(1), move: avgStat('move', us.map((u) => this.stat(u, 'move'))), jump: avgStat('jump', us.map((u) => this.stat(u, 'jump'))),
        brave: Math.round(sum((u) => u.Brave) / n), faith: Math.round(sum((u) => u.Faith) / n),
        evade: Math.round(sum((u) => this.info(u).evade.front) / n),
      };
    },
    bySpeed() { return [...this.me.units].sort((a, b) => (this.stat(b, 'speed') || 0) - (this.stat(a, 'speed') || 0)); },
    // every element any unit absorbs / halves / is immune or weak to, with who
    teamElements() {
      const m = new Map();
      for (const u of this.me.units) for (const [k, label] of ELEMENT_KINDS) for (const el of this.info(u).elements[k]) {
        const key = k + ':' + el; if (!m.has(key)) m.set(key, { k, label, el, names: [] }); m.get(key).names.push(u.Name);
      }
      const order = { weak: 0, absorb: 1, cancel: 2, half: 3, strengthen: 4 };
      return [...m.values()].sort((a, b) => order[a.k] - order[b.k] || b.names.length - a.names.length);
    },
  },
  watch: {
    teamName() { this.vsAt = 0; this.unitAt = 0; this.tab = 'summary'; this.unitCard = null; this.pickKey = null; },
    oddsJobs: { immediate: true, handler() { this.runOdds(); } },
  },
  created() { this._info = new Map(); this._ug = new Map(); },
  mounted() {
    this._mq = window.matchMedia('(max-width: 700px)');
    this._onMq = () => { this.narrow = this._mq.matches; };
    this._onMq();
    this._mq.addEventListener('change', this._onMq);
  },
  beforeUnmount() { if (this._mq) this._mq.removeEventListener('change', this._onMq); },
  methods: {
    show(sec) { return !this.narrow || this.tab === sec; },
    matchInfo(i) { const mm = MAP_RE.exec(this.maps[i] || ''); return { title: TITLES[i], mapName: mm ? mm[2].trim() : (this.maps[i] || '') }; },
    info(u) {
      if (!this._info.has(u)) { let r; try { r = unitInfo(u, this.gameData); } catch (e) { r = { stats: {}, evade: { front: 0, side: 0, back: 0, magic: 0 }, sources: [], elements: { absorb: [], cancel: [], half: [], weak: [], strengthen: [] }, classBase: null }; } this._info.set(u, r); }
      return this._info.get(u);
    },
    stat(u, k) { const v = this.info(u).stats[k]; return v == null ? '–' : v; },
    statTiles(u) { return [['HP', 'hp'], ['MP', 'mp'], ['Spd', 'speed'], ['PA', 'pa'], ['MA', 'ma'], ['Mv', 'move'], ['Jp', 'jump']].map(([k, s]) => ({ k, v: fmtStat(s, this.stat(u, s)), tip: endlessTip(s, this.stat(u, s), u) })).concat([{ k: 'Br', v: u.Brave }, { k: 'Fa', v: u.Faith }]); },
    gear(u) { return gearRows(u, this.gameData); },
    els(u) { return elementChips(this.info(u).elements); },
    sets(u) { return skillSets(u, this.gameData); },
    innates(u) { const cb = this.info(u).classBase; return ((cb && cb.innates) || []).map((i) => i && i.name).filter(Boolean); },
    unitG(u) { if (!this._ug.has(u)) this._ug.set(u, unitGrade(u, rosterStats, this.gameData)); return this._ug.get(u); },
    zod(u, f) { return zodiacCompat(u.Gender, u.Sign, f.Gender, f.Sign) || 'Neutral'; },
    trackColor(u) { const h = this.highlightColors, n = (u.Name || '').toLowerCase(); return (h && n && (h instanceof Map ? h.get(n) : h[n])) || null; },
    portrait(u, label) {
      if (u.Gender === 'Monster') return UNITS_BASE + u.Class + '.gif';
      const g = (u.Gender || 'Male')[0];
      return label === 'Champion' ? UNITS_BASE + u.Class + g + '.gif' : UNITS_BASE + u.Class + g + '_' + label + '.gif';
    },
    // keyed by both rosters too, so a roster change re-runs it
    oddsKeyFor(foeKey, at) {
      const t = this.byName[foeKey];
      return [this.teamName, foeKey, at, ...this.me.units.map((u) => u.Name), ...((t && t.roster) || []).map((u) => u.Name)].join('|');
    },
    // one matchup at a time (each is 300 battles on the worker pool), in oddsJobs order; done ones are kept
    async runOdds() {
      if (this._running) { this._again = true; return; }
      this._running = true;
      try {
        for (;;) {
          this._again = false;
          const job = this.oddsJobs.find(([k, at]) => !this.oddsMap[this.oddsKeyFor(k, at)]);
          if (!job || !this.me.units.length) break;
          const [k, at] = job, key = this.oddsKeyFor(k, at), t = this.byName[k];
          this.oddsMap = { ...this.oddsMap, [key]: 'running' };
          let o = 'failed';
          try {
            const side = { key: t.name, label: t.label, bright: teamBright(t.label), units: t.roster || [] };
            if (side.units.length) o = await cachedMatchOdds(this.me, side, this.maps[at], this.gameData);
          } catch (e) { o = 'failed'; }
          this.oddsMap = { ...this.oddsMap, [key]: o };
        }
      } finally { this._running = false; }
      if (this._again) this.runOdds();
    },
  },
};
</script>

<style scoped>
.tc-back { position: fixed; inset: 0; z-index: 1000; overflow-y: auto; padding: 32px 12px; background: rgba(4, 6, 12, 0.75); }
.tc {
  position: relative; box-sizing: border-box; width: 100%; max-width: 1280px; margin: 0 auto; color: var(--ink); border: 1px solid var(--brass-edge); border-top: 4px solid var(--tc); border-radius: 6px;
  box-shadow: 0 16px 50px rgba(0, 0, 0, 0.6); padding-bottom: 14px; text-align: left;
  background:
    linear-gradient(var(--frame-hi), var(--frame-hi)) left 0 bottom 0 / 14px 2px no-repeat, linear-gradient(var(--frame-hi), var(--frame-hi)) left 0 bottom 0 / 2px 14px no-repeat,
    linear-gradient(var(--frame-hi), var(--frame-hi)) right 0 bottom 0 / 14px 2px no-repeat, linear-gradient(var(--frame-hi), var(--frame-hi)) right 0 bottom 0 / 2px 14px no-repeat,
    linear-gradient(180deg, var(--panel-top), var(--panel-bottom));
}
.tc-bar { display: flex; align-items: center; flex-wrap: wrap; gap: 10px; padding: 10px 16px 6px; }
.tc-name { font-family: var(--display); font-weight: 700; font-size: 30px; line-height: 1; color: var(--tc); filter: brightness(1.3); text-shadow: 0 2px 0 rgba(0, 0, 0, 0.6); }
.tc-grade { font-size: 20px; }
.tc-fate { font-style: italic; color: var(--mute); font-size: 14px; margin-left: auto; }
.tc-x { color: var(--ink); }
.tc-empty { padding: 0 16px; color: var(--mute); font-style: italic; }
.tc-h { margin: 16px 16px 8px; font-family: var(--display); font-size: 17px; color: var(--brass-hi); border-bottom: 1px solid rgba(217, 180, 94, 0.3); padding-bottom: 3px; }
.tc-h small { margin-left: 8px; font-family: var(--cond); font-size: 14px; letter-spacing: 0.4px; color: var(--mute); font-weight: 600; }
.tc-lbl { display: block; font-family: var(--cond); font-weight: 700; font-size: 12px; letter-spacing: 1.4px; text-transform: uppercase; color: var(--brass); margin-bottom: 3px; }

/* summary strip */
.tc-summary { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)) minmax(0, 1.4fr) minmax(0, 2fr); gap: 1px; margin: 4px 16px 0; background: rgba(217, 180, 94, 0.25); border: 1px solid rgba(217, 180, 94, 0.25); border-radius: 4px; overflow: hidden; }
@media (max-width: 1000px) { .tc-summary { grid-template-columns: repeat(2, minmax(0, 1fr)); } .tc-turns, .tc-els { grid-column: span 2; } }
.tc-summary > div { background: var(--navy); padding: 6px 10px; }
.tc-tile { display: flex; flex-direction: column; align-items: center; justify-content: center; }
.tc-tile span { font-family: var(--cond); font-weight: 600; font-size: 11px; letter-spacing: 1px; text-transform: uppercase; color: var(--brass); }
.tc-tile b { font-family: var(--cond); font-weight: 700; font-size: 24px; color: var(--ink-hi); font-variant-numeric: tabular-nums; line-height: 1.1; }
.tc-tile i { font-style: normal; font-size: 11.5px; color: var(--mute); }
.tc-turn { display: block; font-size: 13px; line-height: 1.45; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.tc-turn b { display: inline-block; width: 14px; color: var(--brass); font-family: var(--cond); }
.tc-turn i { font-style: normal; color: var(--mute); font-size: 12px; }
.tc-els { display: flex; flex-wrap: wrap; align-content: flex-start; gap: 4px; }
.tc-els .tc-lbl { flex-basis: 100%; }
.tc-none { font-size: 12.5px; color: var(--mute); font-style: italic; }
.tc-el { padding: 1px 8px; border-radius: 10px; font-size: 12px; background: rgba(255, 255, 255, 0.07); }
.tc-el.absorb, .tc-el.cancel, .tc-el.half { color: #9fe0a6; } .tc-el.weak { color: #f0a49b; } .tc-el.strengthen { color: var(--brass-hi); }
.tc-el.shared.weak { background: rgba(200, 80, 70, 0.22); } .tc-el.shared:not(.weak) { background: rgba(80, 170, 90, 0.16); }

/* the four units */
.tc-units { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 10px; margin: 0 16px; }
@media (max-width: 1100px) { .tc-units { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 560px) {
  .tc-back { padding: 16px 8px; }
  .tc-units { grid-template-columns: minmax(0, 1fr); margin: 0 10px; }
  .tc-summary { margin: 4px 10px 0; }
  .tc-name { font-size: 24px; }
  .tc-fate { flex-basis: 100%; margin-left: 0; order: 5; }
}
.tc-unit { display: flex; flex-direction: column; gap: 3px; padding: 8px 10px; border: 1px solid var(--line); border-top: 3px solid var(--tc); border-radius: 4px; background: rgba(0, 0, 0, 0.18); min-width: 0; }
.tc-unit.boss { box-shadow: inset 0 0 0 1px gold; }
.tc-u-head { display: flex; align-items: center; gap: 8px; background: none; border: 0; padding: 0; color: inherit; font: inherit; text-align: left; cursor: pointer; }
.tc-u-head:hover b { color: var(--brass-hi); }
.tc-u-id { display: flex; flex-wrap: wrap; align-items: baseline; gap: 2px 6px; min-width: 0; }
.tc-u-id b { font-family: var(--cond); font-weight: 700; font-size: 18px; color: var(--ink-hi); overflow-wrap: anywhere; }
.tc-u-id i { flex-basis: 100%; font-style: normal; font-size: 12.5px; color: var(--mute); }
.tc-boss { color: gold; margin-right: 2px; }
.tc-u-stats { display: grid; grid-template-columns: repeat(9, minmax(0, 1fr)); gap: 1px; background: rgba(217, 180, 94, 0.2); border-radius: 3px; overflow: hidden; }
.tc-u-stats span { display: flex; flex-direction: column; align-items: center; background: var(--navy); padding: 2px 0; }
.tc-u-stats i { font-style: normal; font-family: var(--cond); font-size: 10px; letter-spacing: 0.6px; color: var(--brass); }
.tc-u-stats b { font-family: var(--cond); font-size: 15px; color: var(--ink-hi); font-variant-numeric: tabular-nums; }
.tc-u-ev { align-self: flex-start; margin: 3px 0; }
.tc-u-sec { margin-top: 5px; font-family: var(--cond); font-weight: 700; font-size: 11.5px; letter-spacing: 1.3px; text-transform: uppercase; color: var(--mute); }
.tc-gear { display: flex; align-items: center; gap: 6px; font-size: 13px; }
.tc-gear img, .tc-gear-empty { width: 20px; height: 20px; image-rendering: pixelated; flex: none; }
.tc-gear > span:last-child { display: flex; flex-direction: column; min-width: 0; }
.tc-gear b { font-family: var(--cond); font-weight: 600; font-size: 14px; color: var(--ink-hi); }
.tc-gear i { font-style: normal; font-size: 11.5px; color: var(--mute); }
.tc-u-els { display: flex; flex-wrap: wrap; gap: 3px; margin-top: 3px; }
.tc-skill { display: inline-block; margin: 0 3px 3px 0; padding: 0 6px; border: 1px solid #3a4560; border-radius: 3px; font-size: 12.5px; color: var(--ink-hi); }
.tc-rsm { font-size: 13px; color: var(--ink-hi); }
.tc-innate { font-size: 12px; color: var(--mute); }
.tc-u-full { align-self: flex-start; margin-top: auto; }

/* against the opponent */
.tc-vs { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 16px; align-items: center; margin: 0 16px 8px; }
@media (max-width: 760px) { .tc-vs { grid-template-columns: minmax(0, 1fr); } }
.tc-zod { display: block; max-width: 100%; overflow-x: auto; border-collapse: collapse; font-size: 12.5px; }
.tc-zod caption { text-align: left; font-family: var(--cond); font-weight: 700; font-size: 12px; letter-spacing: 1.4px; text-transform: uppercase; color: var(--brass); padding-bottom: 3px; }
.tc-zod th { font-family: var(--cond); font-weight: 600; font-size: 13px; color: var(--ink-hi); padding: 2px 6px; text-align: left; white-space: nowrap; }
.tc-zod th i { display: block; font-style: normal; font-size: 11px; color: var(--mute); font-weight: 400; }
.tc-zod td { text-align: center; padding: 2px 6px; border: 1px solid var(--line); min-width: 44px; color: var(--mute); }
.tc-zod td.best, .tc-zod td.good { color: #9fe0a6; background: rgba(80, 170, 90, 0.14); }
.tc-zod td.bad, .tc-zod td.worst { color: #f0a49b; background: rgba(200, 80, 70, 0.14); }
.tc-zod td.best, .tc-zod td.worst { font-weight: 700; }
.tc-cands { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 8px; margin: 0 16px 6px; }
.tc-cand { display: flex; flex-direction: column; gap: 3px; padding: 7px 10px; text-align: left; cursor: pointer; font: inherit; color: var(--ink);
  background: rgba(0, 0, 0, 0.2); border: 1px solid var(--line); border-left: 4px solid var(--oc); border-radius: 4px; }
.tc-cand:hover { background: rgba(217, 180, 94, 0.08); }
.tc-cand.on { border-color: var(--brass); border-left-color: var(--oc); background: rgba(217, 180, 94, 0.1); }
.tc-cand-name { display: flex; align-items: center; gap: 6px; font-family: var(--cond); font-weight: 700; font-size: 17px; text-transform: uppercase; letter-spacing: 0.6px; color: var(--oc); filter: brightness(1.25); }
.tc-cand-name .lx-grade { font-size: 13px; filter: none; }
.tc-cand-fate { font-size: 12px; font-style: italic; color: var(--mute); }
.tc-cand-run { font-size: 13px; font-style: italic; color: var(--mute); }
.tc-cand-word { font-family: var(--cond); font-weight: 600; font-size: 17px; color: var(--ink-hi); }
.tc-cand-bar { display: flex; height: 6px; border-radius: 3px; overflow: hidden; }
.tc-cand-bar i { display: block; height: 100%; }
@media (max-width: 560px) { .tc-cands { margin: 0 10px 6px; } }
.tc-tabs { display: flex; flex-wrap: wrap; gap: 4px; margin: 0 16px 6px; }
.tc-tab { padding: 2px 10px; border: 1px solid var(--line); border-radius: 3px; background: transparent; color: var(--ink); font-family: var(--cond); font-weight: 600; font-size: 15px; cursor: pointer; }
.tc-sections { position: sticky; top: -16px; z-index: 3; margin: 0; padding: 8px 10px; background: var(--panel-top); border-bottom: 1px solid var(--line); }
.tc-sections .tc-tab { flex: 1 1 auto; }
.tc-tab.on { border-color: var(--brass); color: var(--brass-hi); background: rgba(217, 180, 94, 0.1); }
.tc :deep(.uv.bare) { margin: 0 16px; overflow-x: auto; }
@media (max-width: 560px) { .tc :deep(.uv.bare) { margin: 0 10px; } .tc-vs { margin: 0 10px 8px; } .tc-tabs:not(.tc-sections) { margin: 0 10px 6px; } }

/* its run */
.tc-run { margin: 0 16px; }
.tc-run-row { display: grid; grid-template-columns: 130px minmax(0, 1fr) minmax(0, 1fr) 100px; gap: 10px; padding: 4px 8px; border-bottom: 1px solid var(--line); font-size: 14px; align-items: baseline; }
@media (max-width: 560px) { .tc-run-row { grid-template-columns: minmax(0, 1fr) auto; } .tc-run-map { display: none; } }
.tc-run-round { font-family: var(--cond); font-weight: 700; letter-spacing: 0.6px; text-transform: uppercase; color: var(--brass); }
.tc-run-map { color: var(--mute); font-style: italic; }
.tc-run-res { text-align: right; font-family: var(--cond); font-weight: 700; letter-spacing: 0.6px; text-transform: uppercase; }
.tc-run-row.won .tc-run-res { color: #8fe0a0; } .tc-run-row.lost .tc-run-res { color: #f0a49b; } .tc-run-row.upcoming .tc-run-res { color: var(--mute); }
.tc-reign { margin: 8px 0 0; font-size: 13.5px; color: var(--brass-hi); }
</style>
